import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { feManagementSystemApi } from '../api/fe-management-system-api';
import { api, BySourceTweetsData, WindowTweet } from '../api/af-social-tracker-api';
import TweetCard, { Tweet as TweetCardTweet, TweetAuthor as TweetCardAuthor } from './TweetCard';
import SocialTweetEngagementCharts, { type EngagementChartPoint } from './SocialTweetEngagementCharts';
import {
  createRowTweetBuckets,
  EMPTY_ROW_TWEET_BUCKETS,
  formatStatDelta,
  parseSyncResultBounds,
  parseTweetStatsFromResult,
  type RowTweetBuckets,
  type SyncResultRecord,
  type TweetStatsSnapshot,
  tweetCreatedMs,
} from '../utils/socialSyncDetailHelpers';

/** Engagement charts: include results whose window end falls in this rolling past span. */
const CHART_ROLLING_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const GRID_WITH_PIN = 'grid-cols-[264px_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]';
const GRID_FEED_ONLY = 'grid-cols-[264px_minmax(0,1fr)]';

type SocialSourceCardModel = {
  cardId: string;
  sourceType: string;
  sourceKey: string;
  syncTargetId?: string;
  syncResults?: SyncResultRecord[];
};

type TweetSyncTableRow = {
  resultIndex: number;
  state: string;
  boundsValid: boolean;
  sinceMs: number;
  untilMs: number;
  monitoringSince: Date;
  monitoringUntil: Date;
};

type DetailRowFocusTarget = { resultIndex: number; requestId: number } | null;

type Props = {
  tokenAddress: string | null;
  selectedSourceCard: SocialSourceCardModel | null;
  /** When true: Time Range + Reply + Quote + Feed (Feed has thick left border). When false: Time Range + Feed only. */
  accountHasPinnedTweet: boolean;
  rowFocusTarget: DetailRowFocusTarget;
  formatTimestampToLocaleString: (value?: string | Date | number) => string;
  getStatusChipClassName: (status?: string) => string;
  onSyncResultsPatched: (cardId: string, nextSyncResults: SyncResultRecord[]) => void;
};

const mapWindowTweetToCardTweet = (windowTweet: WindowTweet): TweetCardTweet => {
  const author: TweetCardAuthor = {
    id: windowTweet.author.id,
    userName: windowTweet.author.userName,
    name: windowTweet.author.name ?? undefined,
    profilePicture: windowTweet.author.profilePicture ?? undefined,
    followers: windowTweet.author.followers ?? undefined,
    label: windowTweet.author.label ?? undefined,
  };
  return {
    id: windowTweet.id,
    text: windowTweet.text,
    createdAt: windowTweet.createdAt,
    author,
    viewCount: windowTweet.viewCount,
    likeCount: windowTweet.likeCount,
    bookmarkCount: windowTweet.bookmarkCount,
    retweetCount: windowTweet.retweetCount,
    quoteCount: windowTweet.quoteCount,
    replyCount: windowTweet.replyCount,
  };
};

const StatDeltaCell: React.FC<{
  current: number;
  previous: number | null;
}> = ({ current, previous }) => {
  const { absText, pctText, positive } = formatStatDelta(current, previous);
  const base =
    'block w-full min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-right tabular-nums';
  if (previous === null) {
    return <span className={`${base} text-gray-600`}>—</span>;
  }
  return (
    <span
      className={`${base} ${
        positive === true ? 'text-emerald-400' : positive === false ? 'text-rose-400' : 'text-gray-500'
      }`}
    >
      {absText}
      {pctText ? `\u00A0${pctText}` : ''}
    </span>
  );
};

const TweetStatsBlock: React.FC<{
  stats: TweetStatsSnapshot;
  prev: TweetStatsSnapshot | null;
}> = ({ stats, prev }) => {
  const p = prev;
  const labelCls = 'min-w-0 truncate text-left text-gray-500';
  const valueCls =
    'block min-w-0 overflow-hidden text-ellipsis whitespace-nowrap text-right tabular-nums text-gray-100';
  return (
    <div className="mt-1.5 w-full min-w-0 max-w-full grid grid-cols-3 gap-x-1.5 gap-y-0.5 border-t border-gray-700/80 pt-1.5 text-[10px] leading-snug">
      <span className={labelCls}>Views</span>
      <span className={valueCls}>{stats.views.toLocaleString()}</span>
      <StatDeltaCell current={stats.views} previous={p?.views ?? null} />

      <span className={labelCls}>Likes</span>
      <span className={valueCls}>{stats.likes.toLocaleString()}</span>
      <StatDeltaCell current={stats.likes} previous={p?.likes ?? null} />

      <span className={labelCls}>Retweets</span>
      <span className={valueCls}>{stats.retweets.toLocaleString()}</span>
      <StatDeltaCell current={stats.retweets} previous={p?.retweets ?? null} />

      <span className={labelCls}>Replies</span>
      <span className={valueCls}>{stats.replies.toLocaleString()}</span>
      <StatDeltaCell current={stats.replies} previous={p?.replies ?? null} />

      <span className={labelCls}>Quotes</span>
      <span className={valueCls}>{stats.quotes.toLocaleString()}</span>
      <StatDeltaCell current={stats.quotes} previous={p?.quotes ?? null} />

      <span className={labelCls}>Bookmarks</span>
      <span className={valueCls}>{stats.bookmarks.toLocaleString()}</span>
      <StatDeltaCell current={stats.bookmarks} previous={p?.bookmarks ?? null} />
    </div>
  );
};

const SocialAccountSourceDetailPanel: React.FC<Props> = ({
  tokenAddress,
  selectedSourceCard,
  accountHasPinnedTweet,
  rowFocusTarget,
  formatTimestampToLocaleString,
  getStatusChipClassName,
  onSyncResultsPatched,
}) => {
  const hasPinned = accountHasPinnedTweet;

  const [localSyncResults, setLocalSyncResults] = useState<SyncResultRecord[]>(
    selectedSourceCard?.syncResults ?? [],
  );
  const [tweetAllData, setTweetAllData] = useState<BySourceTweetsData | null>(null);
  const [rowTweetMap, setRowTweetMap] = useState<Record<number, RowTweetBuckets>>({});
  const [preWindowTweets, setPreWindowTweets] = useState<{
    reply: WindowTweet[];
    quote: WindowTweet[];
    feed: WindowTweet[];
  }>({ reply: [], quote: [], feed: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [hoveredRowIdx, setHoveredRowIdx] = useState<number | null>(null);
  const [editingRowIdx, setEditingRowIdx] = useState<number | null>(null);
  const [noteDraft, setNoteDraft] = useState('');
  const [isSavingNote, setIsSavingNote] = useState(false);

  const syncResultRowRefs = useRef<Record<number, HTMLDivElement | null>>({});

  const onSyncResultsPatchedRef = useRef(onSyncResultsPatched);
  onSyncResultsPatchedRef.current = onSyncResultsPatched;

  const selectedSourceCardRef = useRef(selectedSourceCard);
  selectedSourceCardRef.current = selectedSourceCard;

  const accountCardId = selectedSourceCard?.cardId ?? null;
  const accountSourceKey = (selectedSourceCard?.sourceKey ?? '').trim();

  const accountFetchKey = useMemo(() => {
    if (!accountCardId || !accountSourceKey) return null;
    if (selectedSourceCard?.sourceType?.toUpperCase() !== 'X_ACCOUNT') return null;
    return `${accountCardId}::${accountSourceKey}`;
  }, [accountCardId, accountSourceKey, selectedSourceCard?.sourceType]);

  useEffect(() => {
    const card = selectedSourceCardRef.current;
    if (!card?.cardId) return;
    setLocalSyncResults(card.syncResults ?? []);
    setTweetAllData(null);
    setRowTweetMap({});
    setPreWindowTweets({ reply: [], quote: [], feed: [] });
    setEditingRowIdx(null);
    setNoteDraft('');
    setHoveredRowIdx(null);
  }, [selectedSourceCard?.cardId]);

  const tableRows = useMemo((): TweetSyncTableRow[] => {
    if (!localSyncResults?.length) return [];
    return localSyncResults
      .map((r, resultIndex) => ({ r, resultIndex }))
      .filter(({ r }) => {
        const state = ((r.state ?? '') as string).toUpperCase();
        return state === 'COMPLETED' || state === 'SYNCING' || state === 'FAILED';
      })
      .map(({ r, resultIndex }) => {
        const bounds = parseSyncResultBounds(r);
        const state = ((r.state ?? '') as string).toUpperCase();
        return {
          resultIndex,
          state,
          boundsValid: bounds != null,
          sinceMs: bounds?.sinceMs ?? NaN,
          untilMs: bounds?.untilMs ?? NaN,
          monitoringSince: bounds ? new Date(bounds.sinceMs) : new Date(NaN),
          monitoringUntil: bounds ? new Date(bounds.untilMs) : new Date(NaN),
        };
      })
      .sort((a, b) => {
        const aT = Number.isFinite(a.sinceMs) ? a.sinceMs : 0;
        const bT = Number.isFinite(b.sinceMs) ? b.sinceMs : 0;
        return bT - aT;
      });
  }, [localSyncResults]);

  const chronologicalRows = useMemo(() => {
    const valid = tableRows.filter((r) => r.boundsValid);
    return [...valid].sort((a, b) => a.sinceMs - b.sinceMs);
  }, [tableRows]);

  const prevResultIndexByResultIndex = useMemo(() => {
    const m = new Map<number, number | null>();
    chronologicalRows.forEach((row, j) => {
      m.set(row.resultIndex, j === 0 ? null : chronologicalRows[j - 1].resultIndex);
    });
    return m;
  }, [chronologicalRows]);

  const chartPoints = useMemo(() => {
    const now = Date.now();
    const cutoffMs = now - CHART_ROLLING_WINDOW_MS;

    const chartRows = (localSyncResults ?? [])
      .map((r, resultIndex) => ({ r, resultIndex }))
      .filter(({ r }) => ((r.state ?? '') as string).toUpperCase() === 'COMPLETED')
      .map(({ r, resultIndex }) => {
        const bounds = parseSyncResultBounds(r);
        if (!bounds) return null;
        return {
          resultIndex,
          sinceMs: bounds.sinceMs,
          untilMs: bounds.untilMs,
        };
      })
      .filter((row): row is NonNullable<typeof row> => row != null)
      .filter((row) => row.untilMs >= cutoffMs && row.untilMs <= now)
      .sort((a, b) => a.sinceMs - b.sinceMs);

    const views: EngagementChartPoint[] = [];
    const likes: EngagementChartPoint[] = [];
    const retweets: EngagementChartPoint[] = [];
    chartRows.forEach((row, seq) => {
      const raw = localSyncResults[row.resultIndex];
      if (!raw) return;
      const stats = parseTweetStatsFromResult(raw);
      const label = formatTimestampToLocaleString(new Date(row.untilMs));
      const v = stats?.views ?? 0;
      const l = stats?.likes ?? 0;
      const rt = stats?.retweets ?? 0;
      views.push({ index: seq, label, value: v });
      likes.push({ index: seq, label, value: l });
      retweets.push({ index: seq, label, value: rt });
    });
    return { views, likes, retweets };
  }, [localSyncResults, formatTimestampToLocaleString]);

  useEffect(() => {
    if (!tokenAddress || !accountFetchKey) return;
    const card = selectedSourceCardRef.current;
    if (!card?.cardId) return;
    const sourceType = (card.sourceType || '').trim();
    const sourceKey = (card.sourceKey || '').trim();
    if (!sourceType || !sourceKey) return;
    let cancelled = false;

    const refresh = async () => {
      setIsLoading(true);
      setTweetAllData(null);
      setRowTweetMap({});
      setPreWindowTweets({ reply: [], quote: [], feed: [] });

      try {
        const [targetsResult, bySourceResult] = await Promise.allSettled([
          feManagementSystemApi.listSocialSyncTargets(tokenAddress),
          api.getTweetSyncBySource(tokenAddress, sourceType, sourceKey),
        ]);
        if (cancelled) return;

        if (
          bySourceResult.status === 'fulfilled' &&
          bySourceResult.value?.success &&
          bySourceResult.value.data
        ) {
          setTweetAllData(bySourceResult.value.data);
        }

        if (targetsResult.status === 'fulfilled') {
          const freshTargets = targetsResult.value;
          const matchTarget = freshTargets.find((t) => {
            const tType = (t.source_type ?? t.sourceType ?? '').toString().toUpperCase();
            const tKey = (t.source_key ?? t.sourceKey ?? '').toString();
            return tType === sourceType.toUpperCase() && tKey === sourceKey;
          });
          if (matchTarget) {
            const rawResults = (matchTarget.results ?? []) as unknown[];
            const freshSyncResults: SyncResultRecord[] = rawResults
              .filter((r): r is Record<string, unknown> => r != null && typeof r === 'object')
              .map((r) => r as SyncResultRecord);
            setLocalSyncResults(freshSyncResults);
            onSyncResultsPatchedRef.current(card.cardId, freshSyncResults);
          }
        }
      } catch (err) {
        console.error('X_ACCOUNT detail refresh failed:', err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    void refresh();
    return () => {
      cancelled = true;
    };
  }, [tokenAddress, accountFetchKey]);

  useEffect(() => {
    if (!tweetAllData) return;

    const newRowMap: Record<number, RowTweetBuckets> = {};
    const preBuckets = { reply: [] as WindowTweet[], quote: [] as WindowTweet[], feed: [] as WindowTweet[] };

    const replyList = hasPinned ? (tweetAllData.tweets.replyTweets ?? []) : [];
    const quoteList = hasPinned ? (tweetAllData.tweets.quoteTweets ?? []) : [];
    const feedList = tweetAllData.tweets.feedTweets ?? [];

    if (tableRows.length === 0) {
      // REVIEW 상태 등 모니터링 창이 없을 때 — 모든 트윗을 pre-window로
      preBuckets.reply.push(...replyList);
      preBuckets.quote.push(...quoteList);
      preBuckets.feed.push(...feedList);
    } else {
      const validRows = tableRows.filter((row) => row.boundsValid);
      const matchOrder = [...validRows].sort((a, b) => b.sinceMs - a.sinceMs);

      const pushByBucket = (tweet: WindowTweet, bucketName: 'reply' | 'quote' | 'feed') => {
        const ms = tweetCreatedMs(tweet);
        if (!Number.isFinite(ms)) {
          preBuckets[bucketName].push(tweet);
          return;
        }

        let placed = false;
        for (const row of matchOrder) {
          if (ms >= row.sinceMs && ms <= row.untilMs) {
            const key = row.resultIndex;
            if (!newRowMap[key]) {
              newRowMap[key] = createRowTweetBuckets(true);
            }
            const bucket = newRowMap[key];
            if (bucketName === 'reply') bucket.replyTweets.push(tweet);
            else if (bucketName === 'quote') bucket.quoteTweets.push(tweet);
            else bucket.feedTweets.push(tweet);
            placed = true;
            break;
          }
        }
        if (!placed) preBuckets[bucketName].push(tweet);
      };

      replyList.forEach((t) => pushByBucket(t, 'reply'));
      quoteList.forEach((t) => pushByBucket(t, 'quote'));
      feedList.forEach((t) => pushByBucket(t, 'feed'));

      for (const row of tableRows) {
        if (!newRowMap[row.resultIndex]) {
          newRowMap[row.resultIndex] = createRowTweetBuckets(true);
        }
      }
    }

    setRowTweetMap(newRowMap);
    setPreWindowTweets(preBuckets);
  }, [tweetAllData, tableRows, hasPinned]);

  const handleStartNoteEdit = useCallback(
    (resultIndex: number) => {
      const existingNote = ((localSyncResults ?? [])[resultIndex]?.note ?? '') as string;
      setEditingRowIdx(resultIndex);
      setNoteDraft(existingNote.trim());
    },
    [localSyncResults],
  );

  const handleCancelNoteEdit = useCallback(() => {
    setEditingRowIdx(null);
    setNoteDraft('');
  }, []);

  const handleSubmitNote = useCallback(async () => {
    if (editingRowIdx === null || isSavingNote || !selectedSourceCard?.syncTargetId) return;
    setIsSavingNote(true);
    try {
      await feManagementSystemApi.updateSyncResultNote(
        selectedSourceCard.syncTargetId,
        editingRowIdx,
        noteDraft,
      );
      const updated = [...(localSyncResults ?? [])];
      if (updated[editingRowIdx]) {
        updated[editingRowIdx] = {
          ...updated[editingRowIdx],
          note: noteDraft,
        };
      }
      setLocalSyncResults(updated);
      onSyncResultsPatched(selectedSourceCard.cardId, updated);
      setEditingRowIdx(null);
      setHoveredRowIdx(null);
      setNoteDraft('');
    } catch (err) {
      console.error('Account sync note save failed:', err);
    } finally {
      setIsSavingNote(false);
    }
  }, [
    editingRowIdx,
    isSavingNote,
    selectedSourceCard,
    noteDraft,
    localSyncResults,
    onSyncResultsPatched,
  ]);

  useLayoutEffect(() => {
    if (!rowFocusTarget || isLoading) return;
    const idx = rowFocusTarget.resultIndex;
    if (!tableRows.some((r) => r.resultIndex === idx)) return;
    const el = syncResultRowRefs.current[idx];
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    });
  }, [rowFocusTarget, isLoading, tableRows]);

  const gridClass = hasPinned ? GRID_WITH_PIN : GRID_FEED_ONLY;
  const feedHeaderClass = hasPinned
    ? 'px-3 py-2 border-l-4 border-l-gray-500'
    : 'px-3 py-2';
  const feedCellClass = hasPinned
    ? 'min-w-0 px-3 py-3 border-l-4 border-l-gray-500'
    : 'min-w-0 px-3 py-3';

  if (!selectedSourceCard) {
    return (
      <div className="p-6 text-center text-sm text-gray-400">
        소셜 소스 카드를 선택하면 하단에 상세가 표시됩니다.
      </div>
    );
  }

  return (
    <div className="flex min-h-0 flex-col">
      {isLoading && tableRows.length === 0 ? (
        <div className="p-4 text-sm text-gray-500">Loading account sync details...</div>
      ) : null}
      {hasPinned ? (
        <SocialTweetEngagementCharts
          views={chartPoints.views}
          likes={chartPoints.likes}
          retweets={chartPoints.retweets}
        />
      ) : null}

      <div className="mt-0 min-h-0 flex-1 overflow-y-auto">
        <div className={`grid ${gridClass} bg-gray-900/70 border-b border-gray-700 text-xs font-semibold text-gray-300`}>
          <div className="px-3 py-2 border-r border-gray-700">Time Range</div>
          {hasPinned ? (
            <>
              <div className="px-3 py-2 border-r border-gray-700">Reply</div>
              <div className="px-3 py-2 border-r border-gray-700">Quote</div>
            </>
          ) : null}
          <div className={feedHeaderClass}>Feed</div>
        </div>

        <div className="divide-y divide-gray-700">
          {tableRows.length === 0 && (
            <div className="p-4 text-sm text-gray-500">No sync results.</div>
          )}
          {tableRows.map((row) => {
            const rowTweets = rowTweetMap[row.resultIndex] || EMPTY_ROW_TWEET_BUCKETS;
            const hasAny = hasPinned
              ? rowTweets.replyTweets.length > 0 ||
                rowTweets.quoteTweets.length > 0 ||
                rowTweets.feedTweets.length > 0
              : rowTweets.feedTweets.length > 0;
            const isCompactRow = rowTweets.loaded && !rowTweets.isLoading && !hasAny;
            const existingNote = ((localSyncResults ?? [])[row.resultIndex]?.note ?? '') as string;
            const isEditingThisRow = editingRowIdx === row.resultIndex;
            const isHoveringThisRow = hoveredRowIdx === row.resultIndex;
            const raw = localSyncResults[row.resultIndex];
            const stats = raw ? parseTweetStatsFromResult(raw) : null;
            const prevIdx = prevResultIndexByResultIndex.get(row.resultIndex) ?? null;
            const prevRaw = prevIdx != null ? localSyncResults[prevIdx] : null;
            const prevStats = prevRaw ? parseTweetStatsFromResult(prevRaw) : null;

            return (
              <div
                key={`acct-row-${row.resultIndex}-${row.boundsValid ? row.sinceMs : 'invalid'}`}
                ref={(el) => {
                  syncResultRowRefs.current[row.resultIndex] = el;
                }}
                className={`grid ${gridClass} text-sm text-gray-300 bg-gray-900/30 ${
                  isCompactRow ? 'min-h-[40px]' : 'min-h-[120px]'
                }`}
              >
                <div
                  className="relative min-w-0 overflow-x-hidden px-3 py-3 border-r border-gray-700 text-xs leading-tight"
                  onMouseEnter={() => setHoveredRowIdx(row.resultIndex)}
                  onMouseLeave={() => setHoveredRowIdx(null)}
                >
                  <div>
                    {row.boundsValid
                      ? formatTimestampToLocaleString(row.monitoringUntil)
                      : '—'}
                  </div>
                  <div className="text-[10px] text-gray-500 mt-0.5">
                    {row.boundsValid
                      ? `${formatTimestampToLocaleString(row.monitoringSince)} ~`
                      : 'Invalid range'}
                  </div>
                  {row.state && (
                    <span
                      className={`mt-1 inline-block rounded border px-1.5 py-0.5 text-[10px] font-medium ${getStatusChipClassName(
                        row.state,
                      )}`}
                    >
                      {row.state}
                    </span>
                  )}
                  {stats ? <TweetStatsBlock stats={stats} prev={prevStats} /> : null}
                  {isHoveringThisRow && !isEditingThisRow && (
                    <button
                      type="button"
                      className="absolute top-1 right-1 rounded bg-gray-700 px-1.5 py-0.5 text-[10px] text-gray-300 hover:bg-gray-600 transition-colors"
                      onClick={() => handleStartNoteEdit(row.resultIndex)}
                    >
                      ✎
                    </button>
                  )}
                  {existingNote && !isEditingThisRow && (
                    <div className="mt-2 rounded bg-gray-800/70 px-2 py-1.5 text-[11px] text-gray-300 whitespace-pre-wrap break-words leading-relaxed">
                      {existingNote}
                    </div>
                  )}
                  {isEditingThisRow && (
                    <div className="mt-2 space-y-1">
                      <textarea
                        className="w-full rounded border border-gray-600 bg-gray-800 px-2 py-1 text-[11px] text-gray-200 outline-none focus:border-blue-500 resize-none"
                        rows={3}
                        value={noteDraft}
                        onChange={(e) => setNoteDraft(e.target.value)}
                        placeholder="메모 입력..."
                        disabled={isSavingNote}
                      />
                      <div className="flex items-center gap-1">
                        <button
                          type="button"
                          className="rounded bg-blue-600 px-2 py-0.5 text-[10px] text-white hover:bg-blue-500 disabled:opacity-50 transition-colors"
                          onClick={handleSubmitNote}
                          disabled={isSavingNote}
                        >
                          {isSavingNote ? '...' : 'Save'}
                        </button>
                        <button
                          type="button"
                          className="rounded bg-gray-700 px-2 py-0.5 text-[10px] text-gray-300 hover:bg-gray-600 transition-colors"
                          onClick={handleCancelNoteEdit}
                          disabled={isSavingNote}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
                {hasPinned ? (
                  <>
                    <div className="min-w-0 px-3 py-3 border-r border-gray-700">
                      {rowTweets.isLoading ? (
                        <div className="text-xs text-gray-500">Loading...</div>
                      ) : rowTweets.replyTweets.length === 0 ? (
                        <div className="text-gray-500 text-xs">-</div>
                      ) : (
                        <div className="space-y-2 min-w-0">
                          {rowTweets.replyTweets.map((tweet, idx) => (
                            <TweetCard
                              key={`a-reply-${row.resultIndex}-${tweet.id}-${idx}`}
                              tweet={mapWindowTweetToCardTweet(tweet)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="min-w-0 px-3 py-3 border-r border-gray-700">
                      {rowTweets.isLoading ? (
                        <div className="text-xs text-gray-500">Loading...</div>
                      ) : rowTweets.quoteTweets.length === 0 ? (
                        <div className="text-gray-500 text-xs">-</div>
                      ) : (
                        <div className="space-y-2 min-w-0">
                          {rowTweets.quoteTweets.map((tweet, idx) => (
                            <TweetCard
                              key={`a-quote-${row.resultIndex}-${tweet.id}-${idx}`}
                              tweet={mapWindowTweetToCardTweet(tweet)}
                            />
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                ) : null}
                <div className={feedCellClass}>
                  {rowTweets.isLoading ? (
                    <div className="text-xs text-gray-500">Loading...</div>
                  ) : rowTweets.feedTweets.length === 0 ? (
                    <div className="text-gray-500 text-xs">-</div>
                  ) : (
                    <div className="space-y-2 min-w-0">
                      {rowTweets.feedTweets.map((tweet, idx) => (
                        <TweetCard
                          key={`a-feed-${row.resultIndex}-${tweet.id}-${idx}`}
                          tweet={mapWindowTweetToCardTweet(tweet)}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}

          {(tableRows.length > 0 || preWindowTweets.feed.length > 0 || preWindowTweets.reply.length > 0 || preWindowTweets.quote.length > 0) && (
            <div className={`grid min-h-[100px] ${gridClass} text-sm text-gray-300 bg-gray-900/30`}>
              <div className="min-w-0 overflow-x-hidden px-3 py-3 border-r border-gray-700 text-xs leading-tight">
                <div>모니터링 이전</div>
                <div className="text-[10px] text-gray-500">
                  {hasPinned ? '(윈도우 밖 Reply / Quote / Feed)' : '(윈도우 밖 Feed)'}
                </div>
              </div>
              {hasPinned ? (
                <>
                  <div className="min-w-0 px-3 py-3 border-r border-gray-700">
                    {preWindowTweets.reply.length === 0 ? (
                      <div className="text-gray-500 text-xs">-</div>
                    ) : (
                      <div className="space-y-2 min-w-0">
                        {preWindowTweets.reply.map((tweet, idx) => (
                          <TweetCard
                            key={`apre-reply-${tweet.id}-${idx}`}
                            tweet={mapWindowTweetToCardTweet(tweet)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="min-w-0 px-3 py-3 border-r border-gray-700">
                    {preWindowTweets.quote.length === 0 ? (
                      <div className="text-gray-500 text-xs">-</div>
                    ) : (
                      <div className="space-y-2 min-w-0">
                        {preWindowTweets.quote.map((tweet, idx) => (
                          <TweetCard
                            key={`apre-quote-${tweet.id}-${idx}`}
                            tweet={mapWindowTweetToCardTweet(tweet)}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </>
              ) : null}
              <div className={feedCellClass}>
                {preWindowTweets.feed.length === 0 ? (
                  <div className="text-gray-500 text-xs">-</div>
                ) : (
                  <div className="space-y-2 min-w-0">
                    {preWindowTweets.feed.map((tweet, idx) => (
                      <TweetCard
                        key={`apre-feed-${tweet.id}-${idx}`}
                        tweet={mapWindowTweetToCardTweet(tweet)}
                      />
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SocialAccountSourceDetailPanel;
